// src/redux/tripperSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCookie } from '../utils/cookies';
import { logoutUser } from './auth';

const TRIPPER_BASE = `${process.env.REACT_APP_API_URL}/api/tripper`;
const QUESTS_BASE = `${TRIPPER_BASE}/quests`;

// ───────── Thunks ─────────

// fetch profile
export const fetchTripperProfile = createAsyncThunk(
  'tripperUser/fetchProfile',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${TRIPPER_BASE}/profile`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Fetch failed');
    return data;
  }
);

// start quest
export const startQuest = createAsyncThunk(
  'tripperUser/startQuest',
  async (questId, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${QUESTS_BASE}/start/${questId}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data.error || 'Start failed');
    return data;
  }
);

// complete quest
export const completeQuest = createAsyncThunk(
  'tripperUser/completeQuest',
  async (questId, thunkAPI) => {
    const token = thunkAPI.getState().auth.user?.token;
    const res = await fetch(`${QUESTS_BASE}/complete/${questId}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    const data = await res.json();
    if (!res.ok)
      return thunkAPI.rejectWithValue(data.error || 'Complete failed');

    // Refresh profile after some delay on the server side anyway
    return data;
  }
);

// upload avatar
export const uploadTripperAvatar = createAsyncThunk(
  'tripperUser/uploadAvatar',
  async (file, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user?.token;
      const formData = new FormData();
      formData.append('avatar', file);

      const res = await fetch(`${TRIPPER_BASE}/profile/avatar`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
          Authorization: token ? `Bearer ${token}` : undefined
        },
        body: formData
      });

      const data = await res.json();
      if (!res.ok) {
        return thunkAPI.rejectWithValue(data.error || 'Avatar upload failed');
      }
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || 'Avatar upload failed');
    }
  }
);

// ───────── Slice ─────────

const initialState = {
  profile: null,
  status: 'idle',
  error: null
};

const tripperUserSlice = createSlice({
  name: 'tripperUser',
  initialState,
  extraReducers: builder => {
    builder
      // profile
      .addCase(fetchTripperProfile.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTripperProfile.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.profile = payload;
      })
      .addCase(fetchTripperProfile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // start quest
      .addCase(startQuest.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(startQuest.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.profile = payload;
      })
      .addCase(startQuest.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // complete quest
      .addCase(completeQuest.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(completeQuest.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.profile = payload;
      })
      .addCase(completeQuest.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // upload avatar
      .addCase(uploadTripperAvatar.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(uploadTripperAvatar.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.profile = payload;
      })
      .addCase(uploadTripperAvatar.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // logout
      .addCase(logoutUser.fulfilled, () => initialState);
  }
});

export default tripperUserSlice.reducer;